import React, { useState } from "react";
import ModalScreen from "../common/ModalScreen";
import { X } from "lucide-react";

const CancelOfferConfirmation = ({
  offerId,
  openModal,
  onClose,
  handleCancelProposal,
}: {
  offerId: string | null;
  openModal: boolean;
  onClose: () => void;
  handleCancelProposal: (cancelReason: CancelReason) => void;
}) => {
  if (!offerId) {
    return null; // If no offerId is provided, do not render the modal
  }

  const [cancelReason, setCancelReason] = useState({
    reason: "",
    notes: "",
  });

  React.useEffect(() => {
    if (openModal) {
      setCancelReason({ reason: "", notes: "" });
    }
  }, [openModal]);

  const closeModal = () => {
    onClose();
  };

  if (!openModal) return null;

  return (
    <ModalScreen
      title="Cancelar Proposta"
      onClose={closeModal}
      state="confirm"
      footer={
        <div className="flex justify-end gap-2 p-4">
          <button className="button is-white" onClick={closeModal}>
            Voltar
          </button>
          <button
            className="button is-danger"
            disabled={
              !cancelReason.reason ||
              (cancelReason.reason === "other" && !cancelReason.notes.trim())
            }
            onClick={() => {
              handleCancelProposal(cancelReason);
            }}
          >
            <span className="icon is-small">
              <X className="inline mr-1" />
            </span>
            <span>Confirmar Cancelamento</span>
          </button>
        </div>
      }
    >
      <div className="p-6">
        <div className="notification is-warning is-light mb-6 flex items-start">
          <span className="icon has-text-warning mr-3 mt-1">
            <i className="fas fa-exclamation-triangle"></i>
          </span>
          <div>
            <p className="font-bold text-gray-800">
              Tem a certeza que deseja cancelar esta proposta?
            </p>
            <p className="text-sm mt-1 text-gray-600">
              Esta ação não pode ser facilmente revertida. Por favor, forneça um
              motivo para o cancelamento.
            </p>
          </div>
        </div>

        <div className="box shadow-sm border border-gray-100 p-4">
          <div className="field mb-4">
            <label className="label text-xs uppercase tracking-wide font-semibold text-gray-500 mb-2">
              Motivo do Cancelamento
            </label>
            <div className="control">
              <div className="select is-fullwidth">
                <select
                  value={cancelReason.reason}
                  onChange={(e) =>
                    setCancelReason({
                      ...cancelReason,
                      reason: e.target.value,
                    })
                  }
                >
                  <option value="">Selecione um motivo</option>
                  <option value="out_of_stock">Fora de stock</option>
                  <option value="client_choose">
                    Cliente prefere outra opção
                  </option>
                  <option value="price_issue">Problema de preço</option>
                  <option value="technical_issue">Problema técnico</option>
                  <option value="logistics_issue">Problema logístico</option>
                  <option value="no_longer_needed">Já não é necessário</option>
                  <option value="duplicate_offer">Oferta duplicada</option>
                  <option value="internal_error">Erro interno</option>
                  <option value="other">Outro</option>
                </select>
              </div>
            </div>
          </div>

          <div className="field">
            <label className="label text-xs uppercase tracking-wide font-semibold text-gray-500 mb-2">
              Notas Adicionais{" "}
              {cancelReason.reason === "other" && (
                <span className="has-text-danger">*</span>
              )}
            </label>
            <div className="control">
              <textarea
                className="textarea"
                placeholder={
                  cancelReason.reason === "other"
                    ? "Por favor, especifique o motivo..."
                    : "Informações adicionais (opcional)"
                }
                value={cancelReason.notes}
                onChange={(e) =>
                  setCancelReason({
                    ...cancelReason,
                    notes: e.target.value,
                  })
                }
                rows={3}
              ></textarea>
            </div>
          </div>
        </div>
      </div>
    </ModalScreen>
  );
};

export default CancelOfferConfirmation;
